import { useGlobalStore } from "controller";
import type { Dayjs } from "dayjs";
import { atom } from "jotai";
import { dayjs } from "utilz/dayjs";

/**
 * Format both date atoms are stored in. Also the key used for day lookups and
 * scroll anchors in the calendar event list.
 */
export const DATE_KEY_FORMAT = "YYYY-MM-DD";

/**
 * The calendar runs entirely in the MSP's timezone — never the browser's — so
 * the week shown, the day highlighted and the times on each card all agree with
 * what the rest of the product reports.
 */
export const mspNow = (timezone?: string): Dayjs =>
  timezone ? dayjs().tz(timezone) : dayjs();

/** Reads an event's UTC timestamp in the MSP's timezone. */
export const mspInstant = (isoDate: string, timezone?: string): Dayjs =>
  timezone ? dayjs(isoDate).tz(timezone) : dayjs(isoDate);

/**
 * Reads a date key as that calendar day in the MSP's timezone. Parsing it as a
 * local date instead would land on the previous day for offsets behind the
 * browser's.
 */
export const mspDay = (dateKey: string, timezone?: string): Dayjs =>
  timezone ? dayjs.tz(dateKey, timezone) : dayjs(dateKey);

/**
 * Any date inside the week currently rendered by the day strip. Moving weeks
 * shifts this by ±7 days; the date picker snaps it to the day picked.
 */
export const calendarAnchorDateAtom = atom(
  mspNow(useGlobalStore.getState().timezone).format(DATE_KEY_FORMAT),
);

/** Day highlighted in the strip — selecting one scrolls its group into view. */
export const calendarSelectedDateAtom = atom(
  mspNow(useGlobalStore.getState().timezone).format(DATE_KEY_FORMAT),
);
